import { FC, MutableRefObject, useEffect, useRef, useState } from 'react'

import { Breakpoint } from '@mui/material'
import { DialogStyled } from 'components/ModalJustify/ModalJustify.styles'
import Draggable from 'react-draggable'
import { COLORS } from 'utils'

import {
  BoundLimits,
  LimitPercentageSize,
  Element,
  MaximumSizeBounds
} from './Draggable'
import { BackgroundShadow } from './styles/BackgroundShadow'
import styled from 'styled-components'

interface IDraggableDialogProps {
  showModal: boolean
  reference: MutableRefObject<Draggable>
  onClose?: () => void
  width: Breakpoint | false
  disableEscapeKeyDown?: boolean
  children: Element
  style?: React.CSSProperties
}

const resetModalState = (reference: MutableRefObject<Draggable>) => {
  if (reference.current) {
    reference.current.state['x'] = 0
    reference.current.state['y'] = 0
  }
}

const POSITION_OFFSET = { x: '0%', y: '0%' }

const LIMIT_PERCENTAGE_SIZES: LimitPercentageSize = {
  lg: {
    horizontal: 0.15,
    vertical: 0.3
  },
  md: { horizontal: 0.3, vertical: 0.4 },
  sm: { horizontal: 0.4, vertical: 0.4 },
  xl: {
    horizontal: 0.5,
    vertical: 0.3
  }
}

const DraggableDialog: FC<IDraggableDialogProps> = ({
  showModal,
  children,
  width,
  disableEscapeKeyDown,
  reference,
  onClose,
  style
}) => {
  const LIMIT_PERCENTAGE: BoundLimits =
    LIMIT_PERCENTAGE_SIZES[width as Breakpoint]

  const [maxSizeBounds, setMaxSizeBounds] = useState<MaximumSizeBounds>({
    bottom:
      window.innerHeight - window.innerHeight * LIMIT_PERCENTAGE.vertical - 40,
    left:
      -window.innerWidth + window.innerWidth * LIMIT_PERCENTAGE.horizontal - 40,
    right:
      window.innerWidth - window.innerWidth * LIMIT_PERCENTAGE.horizontal + 40,
    top:
      -window.innerHeight + window.innerHeight * LIMIT_PERCENTAGE.vertical + 40
  })

  const handleResize = () => {
    setMaxSizeBounds({
      bottom:
        window.innerHeight -
        window.innerHeight * LIMIT_PERCENTAGE.vertical -
        40,
      left:
        -window.innerWidth +
        window.innerWidth * LIMIT_PERCENTAGE.horizontal -
        70,
      right:
        window.innerWidth -
        window.innerWidth * LIMIT_PERCENTAGE.horizontal +
        70,
      top:
        -window.innerHeight +
        window.innerHeight * LIMIT_PERCENTAGE.vertical +
        40
    })
  }

  useEffect(() => {
    window.addEventListener('resize', handleResize, false)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return (
    <BackgroundShadow isOpen={showModal} style={style}>
      <Draggable
        ref={reference}
        cancel=".handler"
        bounds={{
          bottom: maxSizeBounds.bottom,
          left: maxSizeBounds.left,
          right: maxSizeBounds.right,
          top: maxSizeBounds.top
        }}
        positionOffset={POSITION_OFFSET}
      >
        <DialogStyled
          open={showModal}
          onClose={onClose}
          disableEscapeKeyDown={disableEscapeKeyDown}
          BackdropProps={{
            style: {
              backgroundColor: 'transparent'
            }
          }}
          fullWidth
          maxWidth={width}
        >
          <div style={{ cursor: 'move', padding: '0.7rem' }}>
            <div
              style={{ backgroundColor: `${COLORS.white}`, cursor: 'auto' }}
              className="handler"
            >
              {children}
            </div>
          </div>
        </DialogStyled>
      </Draggable>
    </BackgroundShadow>
  )
}

export { DraggableDialog, resetModalState }
